import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import httpService from '../../services/httpService';
import { ApiEndpoints, Constants, ImportTemplates } from '../../constants/appConstants';
import { useDataManagementFileUpload } from '../Common/useDataManagementFileUpload';
import { useExcelImportPopup } from '../Common/useExcelImportPopup';
import DataManagementLoader from './DataManagementLoader';
import DataManagementDataTable from './DataManagementDataTable';
import { createDataManagementQuery, useDataManagementPagination } from './useDataManagementPagination';
import DataManagementForm from './DataManagementForm';
import { dataManagementFormConfig, getFormValidationErrors, normalizeFieldValues } from './dataManagementFormConfig';

interface ReviewConfiguration {
  reviewConfigurationId: number;
  reviewCycleName: string;
  reviewCycleDescription: string;
  reviewYear: number | null;
  reviewYearType: string;
  tieringStartDate: string | null;
  tieringEndDate: string | null;
  isActive: boolean | null;
  isCurrent: boolean | null;
  reviewCounter: number | null;
  contractStartDate: string | null;
  contractEndDate: string | null;
  providerTypeId: number | null;
  providerType: string | null;
  submissionWindow: string | null;
  decisionLetterSent: string | null;
  resolutionWindow: string | null;
  resolutionWindowSent: string | null;
}

interface ProviderType {
  providerTypeId: number;
  providerType: string;
}

type EditValues = Pick<ReviewConfiguration, 'reviewCycleName' | 'reviewCycleDescription' | 'reviewYearType' | 'providerTypeId'> & {
  isCurrent: boolean;
  reviewYear: string;
  reviewCounter: string;
  tieringStartDate: string;
  tieringEndDate: string;
  contractStartDate: string;
  contractEndDate: string;
};

const displayDate = (value: string | null) => value ? value.slice(0, 10) : '-';
const deleteTitle = (config: ReviewConfiguration) => {
  if (config.isCurrent) return 'Current review configuration cannot be deleted';
  if (config.isActive === false) return 'Already inactive';
  return 'Delete review configuration';
};
const errorMessage = (error: unknown, fallback: string) => axios.isAxiosError<{ message?: string }>(error)
  ? error.response?.data?.message || fallback : fallback;

const headerStyle = { padding: 8, border: '1px solid #b0b6be', background: '#f0f4f8', minWidth: 100, textAlign: 'center' as const };
const cellStyle = { padding: 8, border: '1px solid #eee', textAlign: 'center' as const };
const numberHeaderStyle = { ...headerStyle, width: 64, minWidth: 40 };
const numberCellStyle = { ...cellStyle, width: 64, minWidth: 40 };

const renderCurrentStatus = (config: ReviewConfiguration) => (
  <Checkbox checked={config.isCurrent === true} disabled style={{ opacity: 1}}
    aria-label={`Is Current for ${config.reviewCycleName}: ${config.isCurrent === true ? 'yes' : 'no'}`} />
);

const renderReviewConfigurationActions = (config: ReviewConfiguration, onEdit: (config: ReviewConfiguration) => void, onDelete: (config: ReviewConfiguration) => void) => (
  <>
    <Button icon="pi pi-pencil" className="p-button-text" aria-label={`Edit ${config.reviewCycleName}`} title={config.isActive === false ? 'Inactive configuration can be updated through import' : 'Edit review configuration'} disabled={config.isActive === false} onClick={() => onEdit(config)} />
    <Button icon="pi pi-trash" className="p-button-text p-button-danger" aria-label={`Delete ${config.reviewCycleName}`} title={deleteTitle(config)} disabled={config.isCurrent === true || config.isActive === false} onClick={() => onDelete(config)} />
  </>
);

const ReviewConfigurationComponent = () => {
  const [saving, setSaving] = useState(false);
  const [providers, setProviders] = useState<ProviderType[]>([]);
  const [selected, setSelected] = useState<ReviewConfiguration | null>(null);
  const [editValues, setEditValues] = useState<EditValues | null>(null);
  const [toDelete, setToDelete] = useState<ReviewConfiguration | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [providerFilter, setProviderFilter] = useState('');
  const query = createDataManagementQuery({ providerTypeId: providerFilter, search: searchTerm });
  const {
    items: configurations,
    totalRecords: totalCount,
    loading,
    error,
    page,
    setPage,
    refresh,
  } = useDataManagementPagination<ReviewConfiguration>(ApiEndpoints.GetAllReviewConfigurations, query, {
    debounceMs: searchTerm ? 300 : 0,
    errorMessage: 'Failed to fetch review configurations.',
    preserveItemsOnError: true,
    recoverEmptyPageWhenNoResults: true,
  });
  const toast = useRef<Toast>(null);
  const { uploadFile } = useDataManagementFileUpload();

  useEffect(() => {
    httpService.get<ProviderType[]>(ApiEndpoints.GetProviderTypes)
      .then(response => setProviders(response.data))
      .catch(() => toast.current?.show({ severity: 'warn', summary: 'Provider types unavailable', life: 4000 }));
  }, []);

  const openEdit = (config: ReviewConfiguration) => {
    setSelected(config);
    setEditValues({
      reviewCycleName: config.reviewCycleName,
      reviewCycleDescription: config.reviewCycleDescription,
      reviewYear: String(config.reviewYear ?? ''),
      reviewYearType: config.reviewYearType,
      tieringStartDate: config.tieringStartDate?.slice(0, 10) ?? '',
      tieringEndDate: config.tieringEndDate?.slice(0, 10) ?? '',
      contractStartDate: config.contractStartDate?.slice(0, 10) ?? '',
      contractEndDate: config.contractEndDate?.slice(0, 10) ?? '',
      providerTypeId: config.providerTypeId,
      reviewCounter: String(config.reviewCounter ?? ''),
      isCurrent: config.isCurrent === true,
    });
    loadProviderTypes();
  };

  const loadProviderTypes = () => {
    setProviders([]);
    httpService.get<ProviderType[]>(ApiEndpoints.GetProviderTypes)
      .then(response => setProviders(response.data))
      .catch(() => toast.current?.show({ severity: 'warn', summary: 'Provider types unavailable', detail: 'Existing provider type is retained.', life: 4000 }));
  };

  const openAdd = () => {
    setSelected(null);
    setEditValues({
      reviewCycleName: '', reviewCycleDescription: '', reviewYear: '', reviewYearType: '',
      tieringStartDate: '', tieringEndDate: '', contractStartDate: '', contractEndDate: '',
      providerTypeId: null, reviewCounter: '1', isCurrent: false,
    });
    loadProviderTypes();
  };

  const saveEdit = async () => {
    if (!editValues) return;
    const year = Number(editValues.reviewYear);
    const counter = Number(editValues.reviewCounter);
    const validationErrors = getFormValidationErrors(dataManagementFormConfig.reviewConfigurations, editValues);
    if (editValues.reviewCycleName.trim().length > 10)
      validationErrors.push('Review Cycle Name cannot exceed 10 characters');
    if (editValues.reviewYearType.trim().length > 32)
      validationErrors.push('Review Year Type cannot exceed 32 characters');
    if (editValues.reviewYear.trim() && (!Number.isInteger(year) || year < 1 || year > 9999))
      validationErrors.push('Review Year must be a whole number between 1 and 9999');
    if (editValues.reviewCounter && (!Number.isInteger(counter) || counter < 1))
      validationErrors.push('Review Counter must be a positive whole number');
    if (editValues.tieringStartDate && editValues.tieringEndDate && editValues.tieringStartDate > editValues.tieringEndDate)
      validationErrors.push('Tiering End Date cannot be before Tiering Start Date');
    if (editValues.contractStartDate && editValues.contractEndDate && editValues.contractStartDate > editValues.contractEndDate)
      validationErrors.push('Contract End Date cannot be before Contract Start Date');
    if (validationErrors.length > 0) {
      toast.current?.show({ severity: 'warn', summary: 'Fix the following', detail: validationErrors.join(', '), life: 4000 });
      return;
    }
    setSaving(true);
    try {
      const normalized = normalizeFieldValues(dataManagementFormConfig.reviewConfigurations, editValues);
      const payload = {
        ...normalized,
        reviewYear: year,
        reviewCounter: counter,
        providerTypeId: normalized.providerTypeId ? Number(normalized.providerTypeId) : null,
      };
      if (selected) {
        await httpService.put(`${ApiEndpoints.UpdateReviewConfiguration}/${selected.reviewConfigurationId}`, payload);
      } else {
        await httpService.post(ApiEndpoints.AddReviewConfiguration, payload);
      }
      setSelected(null);
      setEditValues(null);
      refresh();
      toast.current?.show({ severity: 'success', summary: selected ? 'Review configuration updated' : 'Review configuration added', life: 3000 });
    } catch (cause) {
      toast.current?.show({ severity: 'error', summary: 'Save failed', detail: errorMessage(cause, 'Unable to save review configuration.'), life: 4000 });
    } finally {
      setSaving(false);
    }
  };
  

  const deleteConfiguration = async () => {
    if (!toDelete) return;
    setSaving(true);
    try {
      await httpService.delete(`${ApiEndpoints.DeleteReviewConfiguration}/${toDelete.reviewConfigurationId}`);
      setToDelete(null);
      refresh();
      toast.current?.show({ severity: 'success', summary: 'Review configuration deleted', life: 3000 });
    } catch (cause) {
      toast.current?.show({ severity: 'error', summary: 'Delete failed', detail: errorMessage(cause, 'Unable to delete review configuration.'), life: 4000 });
    } finally {
      setSaving(false);
    }
  };

  const importConfigurations = async (file: File) => {
    if (!file.name.toLowerCase().endsWith('.xlsx') || file.size === 0)
      throw new Error('Select a non-empty .xlsx file.');
    try {
      const fileKey = await uploadFile(file, Constants.DATA_MANAGEMENT_MODULES.REVIEW_CONFIGURATIONS);
      await httpService.post(ApiEndpoints.BulkUploadDataManagement, {
        fileKey,
        fileName: file.name,
        module: Constants.DATA_MANAGEMENT_MODULES.REVIEW_CONFIGURATIONS,
      });
      refresh();
    } catch (cause) {
      throw new Error(errorMessage(cause, 'Unable to import review configurations.'));
    }
  };

  const { showImport, ImportPopup } = useExcelImportPopup({
    templateUrl: ImportTemplates.reviewConfigurations,
    onUpload: importConfigurations,
    title: 'Import Review Configurations',
    prominentDownloadLink: true,
  });

  const providerOptions = [...providers].sort((left, right) => left.providerType.localeCompare(right.providerType));

  return (
    <div style={{ marginTop: 24, marginBottom: 24, position: 'relative', minHeight: 220 }} aria-busy={loading}>
      <DataManagementLoader visible={loading} label="Loading review configurations" />
      <Toast ref={toast} position="top-right" />
      {error && <div role="alert" style={{ color: 'red' }}>{error}</div>}
      {ImportPopup}
      <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', marginBottom: 16, gap: 16 }}>
        <div style={{ display: 'flex', gap: 12 }}>
          <Button label="Add New Configuration" icon="pi pi-plus" onClick={openAdd} />
          <Button label="Import" icon="pi pi-upload" className="p-button-secondary" onClick={showImport} />
        </div>
        <div className="tier-search-anim">
          <input type="search" aria-label="Search review configurations" placeholder="Search by Review Cycle Name..." maxLength={100} value={searchTerm} onChange={event => { setSearchTerm(event.target.value); setPage(1); }} />
          <span className="pi pi-search" />
        </div>
        <div className="provider-type-dropdown">
          <select aria-label="Filter by provider type" value={providerFilter} onChange={event => { setProviderFilter(event.target.value); setPage(1); }}>
            <option value="">All Provider Types</option>
            {providerOptions.map(provider => <option key={provider.providerTypeId} value={provider.providerTypeId}>{provider.providerType}</option>)}
          </select>
          <span className="pi pi-angle-down" />
        </div>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <DataManagementDataTable value={configurations} dataKey="reviewConfigurationId" page={page} totalRecords={totalCount}
          onPage={setPage} loading={loading} emptyMessage="No review configurations found."
          tableStyle={{ minWidth: 'max-content', background: '#fff' }}>
          <Column header="#" headerStyle={numberHeaderStyle} bodyStyle={numberCellStyle} body={(_config: ReviewConfiguration, options) => options.rowIndex + 1} />
          <Column field="reviewCycleName" header="Review Cycle Name" headerStyle={headerStyle} bodyStyle={cellStyle} body={(config: ReviewConfiguration) => config.reviewCycleName || '-'} />
          <Column field="reviewCycleDescription" header="Description" headerStyle={headerStyle} bodyStyle={cellStyle} body={(config: ReviewConfiguration) => config.reviewCycleDescription || '-'} />
          <Column field="reviewYear" header="Review Year" headerStyle={headerStyle} bodyStyle={cellStyle} body={(config: ReviewConfiguration) => config.reviewYear ?? '-'} />
          <Column field="reviewYearType" header="Review Year Type" headerStyle={headerStyle} bodyStyle={cellStyle} body={(config: ReviewConfiguration) => config.reviewYearType || '-'} />
          <Column header="Tiering Start Date" headerStyle={headerStyle} bodyStyle={cellStyle} body={(config: ReviewConfiguration) => displayDate(config.tieringStartDate)} />
          <Column header="Tiering End Date" headerStyle={headerStyle} bodyStyle={cellStyle} body={(config: ReviewConfiguration) => displayDate(config.tieringEndDate)} />
          <Column header="Contract Start Date" headerStyle={headerStyle} bodyStyle={cellStyle} body={(config: ReviewConfiguration) => displayDate(config.contractStartDate)} />
          <Column header="Contract End Date" headerStyle={headerStyle} bodyStyle={cellStyle} body={(config: ReviewConfiguration) => displayDate(config.contractEndDate)} />
          <Column field="providerType" header="Provider Type" headerStyle={headerStyle} bodyStyle={cellStyle} body={(config: ReviewConfiguration) => config.providerType || '-'} />
          <Column field="reviewCounter" header="Review Counter" headerStyle={headerStyle} bodyStyle={cellStyle} body={(config: ReviewConfiguration) => config.reviewCounter ?? '-'} />
          <Column field="submissionWindow" header="Submission Window" headerStyle={headerStyle} bodyStyle={cellStyle} body={(config: ReviewConfiguration) => config.submissionWindow || '-'} />
          <Column field="decisionLetterSent" header="Decision Letter Sent" headerStyle={headerStyle} bodyStyle={cellStyle} body={(config: ReviewConfiguration) => config.decisionLetterSent || '-'} />
          <Column field="resolutionWindow" header="Resolution Window" headerStyle={headerStyle} bodyStyle={cellStyle} body={(config: ReviewConfiguration) => config.resolutionWindow || '-'} />
          <Column field="resolutionWindowSent" header="Resolution Window Sent" headerStyle={headerStyle} bodyStyle={cellStyle} body={(config: ReviewConfiguration) => config.resolutionWindowSent || '-'} />
          <Column field="isCurrent" header="Current Review" headerStyle={headerStyle} bodyStyle={cellStyle} body={renderCurrentStatus} />
          <Column header="Actions" headerStyle={headerStyle} bodyStyle={cellStyle} body={(config: ReviewConfiguration) => renderReviewConfigurationActions(config, openEdit, setToDelete)} />
        </DataManagementDataTable>
      </div>
      <Dialog
        header={selected ? `Edit Review Configuration - ${selected.reviewCycleName}` : 'Add Review Configuration'}
        visible={!!editValues}
        style={{ width: '640px', maxWidth: '90vw' }}
        onHide={() => { if (!saving) { setSelected(null); setEditValues(null); } }}
        footer={
          <div>
            <Button label="Cancel" className="p-button-text" disabled={saving} onClick={() => { setSelected(null); setEditValues(null); }} />
            <Button label="Save" icon="pi pi-check" loading={saving} onClick={saveEdit} />
          </div>
        }
      >
        {editValues && (
          <DataManagementForm
            fields={dataManagementFormConfig.reviewConfigurations}
            values={editValues}
            onChange={values => setEditValues(values as EditValues)}
            options={{ providerTypeId: [
              ...(selected?.providerTypeId != null && !providers.some(provider => provider.providerTypeId === selected.providerTypeId)
                ? [{ label: selected.providerType || `Provider ${selected.providerTypeId}`, value: selected.providerTypeId }] : []),
              ...providers.map(provider => ({ label: provider.providerType, value: provider.providerTypeId })),
            ] }}
            isEditing={!!selected}
          />
        )}
      </Dialog>
      <Dialog
        header="Confirm Delete"
        visible={!!toDelete}
        style={{ width: '350px', maxWidth: '90vw' }}
        onHide={() => { if (!saving) setToDelete(null); }}
        footer={
          <div>
            <Button label="No" className="p-button-secondary" disabled={saving} onClick={() => setToDelete(null)} />
            <Button label="Yes" className="p-button-danger" loading={saving} onClick={deleteConfiguration} />
          </div>
        }
      >
        Are you sure you want to delete this review configuration{toDelete ? `: ${toDelete.reviewCycleName}` : ''}?
      </Dialog>
    </div>
  );
};

export default ReviewConfigurationComponent;